import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { graphql, useStaticQuery } from 'gatsby';
import cn from 'classnames';
import useObserver from '../../hooks/useObserver';
import { FeatureBlob } from '../../assets/illustrations';

interface Feature {
  title: string;
  description: string;
  image: IGatsbyImageData;
}

interface FeatureItemProps {
  feature: Feature;
  index: number;
  totalFeatures: number;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  feature,
  index,
  totalFeatures,
}) => {
  const [imgPos, setImgPos] = useState('0');
  const [cardEntry, cardRef] = useObserver({ threshold: 0 });

  const setImagePosition = useCallback(() => {
    if (typeof window === 'undefined') return;

    if (cardRef.current && window.innerWidth > 768) {
      const imageEl = cardRef.current.getBoundingClientRect();
      const windowMid = window.innerHeight / 2;
      const imageMid = imageEl.height / 2 + imageEl.y;
      const imageOffset = (windowMid - imageMid) / 10;
      setImgPos(() => imageOffset.toFixed());
    } else {
      setImgPos('0');
    }
  }, [cardRef.current]);

  useEffect(() => {
    if (cardEntry?.isIntersecting) {
      window.addEventListener('scroll', setImagePosition, true);
      return () => {
        window.removeEventListener('scroll', setImagePosition, true);
      };
    }

    return () => undefined;
  }, [cardEntry?.isIntersecting]);

  return (
    <article
      className={cn(
        'relative md:min-h-screen lg:min-h-[90vh] 2xl:min-h-[80vh]',
        {
          'pb-20 -mb-20 md:pb-40 md:-mb-40': index < totalFeatures - 1,
          'pt-20 -mt-20 md:pt-40 md:-mt-40': index > 0,
        },
      )}
      ref={cardRef as any}
    >
      <FeatureBlob index={index} />

      <div
        className={cn(
          'container relative flex flex-col md:flex-row gap-6 md:gap-10 h-full md:items-center z-10',
          {
            'md:flex-row-reverse': index % 2 === 0,
            'md:flex-row': index % 2 !== 0,
          },
        )}
      >
        <div className="justify-center flex-[2]">
          <h2 className="mb-2 text-2xl font-bold text-blue-500 md:text-4xl">
            {feature.title}
          </h2>

          <p className="md:text-lg">{feature.description}</p>
        </div>

        <div className="flex-[3]">
          <GatsbyImage
            className={cn(
              'w-full transition-opacity duration-1000',
              cardEntry?.isIntersecting ? 'opacity-100' : 'opacity-0',
            )}
            image={feature.image}
            alt={feature.title}
            style={{
              transform: `translateY(${imgPos}px)`,
            }}
          />
        </div>
      </div>
    </article>
  );
};

export const FeaturesSection = () => {
  const { images } = useStaticQuery(graphql`
    query featuresSection {
      images: allFile(
        filter: { relativeDirectory: { eq: "homepage/features" } }
      ) {
        edges {
          node {
            name
            childImageSharp {
              gatsbyImageData(width: 800, placeholder: NONE)
            }
          }
        }
      }
    }
  `);

  const features = useMemo(() => {
    const featureImages = images.edges.reduce((acc: any, { node }: any) => {
      acc[node.name] = node.childImageSharp.gatsbyImageData;

      return acc;
    }, {});

    return [
      {
        title: 'Share Your Travel Calendar with Your Nomad Network',
        description:
          "Coordinate with friends by posting your travel plans to Nomadago's calendar. View their itineraries and plan shared adventures effortlessly. It's seamless travel planning, all in one place.",
        image: featureImages.feature_1,
      },
      {
        title: 'See Where Your Friends Are and Where They’re Going',
        description:
          "Our interactive map allows you to see where your friends are now and where they're headed. Connect across the globe and let the map guide your next rendezvous. Traveling solo? Not with Nomadago.",
        image: featureImages.feature_2,
      },
      {
        title: 'Get Notified When Friends Are Nearby',
        description:
          'Stay in the loop with notifications when you and your friends are in the same destination or when your plans are going to overlap. A surprise meetup or a planned adventure, Nomadago keeps you connected.',
        image: featureImages.feature_3,
      },
      {
        title: 'Browse Verified Coliving and Coworking Spaces',
        description:
          "Find the best places to live and work with Nomadago's curated list of global colivings. Browse on the map, or search by destination to find the ideal environment for your nomadic lifestyle. Work, live, and thrive – all with Nomadago.",
        image: featureImages.feature_4,
      },
    ];
  }, []);

  return (
    <section>
      {features.map((feature, index) => (
        <FeatureItem
          key={feature.title}
          feature={feature}
          index={index}
          totalFeatures={features.length}
        />
      ))}
    </section>
  );
};
